/**
 * GraphViewScreen — Grafo de Conhecimento do repositório (Graphify), por projeto.
 *
 * Fase 2: o grafo é construído automaticamente ao mapear o GitHub de um projeto e
 * fica em /graphs/<project_id>/graph.html (servido pelo graphify-viz). Aqui o
 * usuário escolhe o projeto e vê o grafo force-directed embutido (como o
 * "See it in action" da Graphify).
 *
 * A viz em si vem do componente reutilizável RepoGraphEmbed (mesma peça usada
 * dentro da Jornada, no passo "Análise do Repositório").
 */
import { useState, useEffect } from 'react';
import { Share2, RefreshCw, Loader2 } from 'lucide-react';
import { C } from '@/theme/palette';
import { useRecentProjects } from '@/api/dashboard';
import { useGitRepositories, useStartIndexing, useIndexingStatus } from '@/api/git';
import { RepoGraphEmbed, GRAPHIFY_VIZ_BASE } from '@/components/RepoGraphEmbed';

const _INDEX_DONE = new Set(['done', 'completed', 'failed', 'cancelled']);

export function GraphViewScreen() {
  const { data: projects } = useRecentProjects();
  const items = Array.isArray(projects) ? projects : (projects?.items ?? []);
  const [projectId, setProjectId] = useState('');

  // Repositório do projeto → indexação novamente (reconstrói o grafo).
  const { data: repos } = useGitRepositories(projectId);
  const repoList = Array.isArray(repos) ? repos : (repos?.items ?? []);
  const repoId = repoList[0]?.id ?? null;

  const startIndexing = useStartIndexing(projectId);
  const [indexing, setIndexing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const idxStatus = useIndexingStatus(projectId, repoId, indexing);
  const status = idxStatus.data?.status;
  const progress = idxStatus.data?.progress;

  // Ao concluir a indexação: para o polling e recarrega o grafo (se deu certo).
  useEffect(() => {
    if (indexing && status && _INDEX_DONE.has(status)) {
      setIndexing(false);
      if (status === 'done' || status === 'completed') setReloadKey((k) => k + 1);
    }
  }, [indexing, status]);

  const reindex = async () => {
    if (!repoList.length) return;
    const n = repoList.length;
    if (!window.confirm(
      `Indexar novamente ${n} repositório${n === 1 ? '' : 's'} deste projeto? `
      + 'Os grafos separados (por repo) e o grafo unificado do projeto serão reconstruídos.'
    )) return;
    try {
      // Dispara a (re)indexação de TODOS os repos do projeto. Cada conclusão
      // reconstrói o grafo unificado; ao final, ele reflete todos os repos.
      for (const r of repoList) {
        await startIndexing.mutateAsync(r.id);
      }
      setIndexing(true);
    } catch { /* erro no estado da mutation */ }
  };

  const busy = startIndexing.isPending || indexing;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Cabeçalho + seletor de projeto */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '16px 24px', borderBottom: `1px solid ${C.blackBorder}`,
          flexShrink: 0, flexWrap: 'wrap',
        }}
      >
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          <div
            style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              backgroundColor: `${C.cyber}18`, border: `1px solid ${C.cyber}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            <Share2 size={20} style={{ color: C.cyber }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: C.neutral, margin: 0, letterSpacing: '-0.02em' }}>
              Grafo do Repositório
            </h1>
            <div style={{ fontSize: 12.5, color: C.neutralDim, marginTop: 2 }}>
              Grafo de conhecimento (Graphify) — construído ao mapear o GitHub do projeto.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
          <label htmlFor="graph-project" style={{ fontSize: 12, color: C.neutralDim }}>Projeto:</label>
          <select
            id="graph-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={{
              padding: '8px 10px', backgroundColor: C.blackElev, color: C.neutral,
              border: `1px solid ${C.blackBorder}`, borderRadius: 6, fontSize: 13, minWidth: 200,
            }}
          >
            <option value="">Selecione um projeto…</option>
            {items.map((p) => (
              <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
            ))}
          </select>

          {/* Indexar novamente — reconstrói o grafo de TODOS os repos + o unificado */}
          {projectId && (
            <button
              onClick={reindex}
              disabled={!repoList.length || busy}
              title={repoList.length
                ? `Reindexar ${repoList.length} repositório(s) e reconstruir os grafos (separados + unificado)`
                : 'Conecte um repositório ao projeto (Jornada → Repositório Git)'}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                backgroundColor: busy ? C.blackElev : `${C.cyber}18`,
                color: (!repoList.length || busy) ? C.neutralMuted : C.cyber,
                border: `1px solid ${(!repoList.length || busy) ? C.blackBorder : `${C.cyber}44`}`,
                borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: (!repoList.length || busy) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {busy
                ? <Loader2 size={14} style={{ animation: 'navaSpin 1s linear infinite' }} aria-hidden="true" />
                : <RefreshCw size={14} aria-hidden="true" />}
              {busy
                ? `Indexando…${typeof progress === 'number' ? ` ${Math.round(progress)}%` : ''}`
                : 'Indexar novamente'}
            </button>
          )}
        </div>
      </div>
      {startIndexing.isError && (
        <div style={{ padding: '6px 24px', fontSize: 12, color: C.hot, flexShrink: 0 }}>
          Falha ao iniciar a indexação (sem permissão ou repositório indisponível?).
        </div>
      )}

      {/* Viz embutida (fonte única: RepoGraphEmbed) */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '12px 24px 0' }}>
        {projectId ? (
          <RepoGraphEmbed key={reloadKey} projectId={projectId} fill />
        ) : (
          <div style={{ padding: 40, color: C.neutralDim, maxWidth: 640 }}>
            <p style={{ fontSize: 14, marginBottom: 8 }}>
              Selecione um projeto acima para ver o grafo do repositório mapeado.
            </p>
            <p style={{ fontSize: 12.5, color: C.neutralMuted }}>
              O grafo é construído automaticamente quando você conecta e indexa o repositório do GitHub
              do projeto (etapa <b style={{ color: C.neutralDim }}>Repositório Git</b> da Jornada).
              Se o projeto ainda não tiver grafo, esta área ficará vazia.
            </p>
          </div>
        )}
      </div>

      <div style={{ padding: '8px 24px', borderTop: `1px solid ${C.blackBorder}`, flexShrink: 0 }}>
        <span style={{ fontSize: 11.5, color: C.neutralMuted, fontFamily: 'monospace' }}>
          Fonte: {GRAPHIFY_VIZ_BASE}/&lt;project_id&gt;/graph.html — gerado por indexação do repo (Graphify, sem LLM).
        </span>
      </div>
    </div>
  );
}
